/**
 * Ensures that all forms created during the same visit
 * use consistent tracking data, merging later-arriving params
 * into the snapshot before persistence.
 */

const TRACKING_SNAPSHOT_KEY = "trackingSnapshot:v1";
const FORM_CREATE_COUNT_KEY = "formCreateCount:v1";
const VISIT_ID_KEY = "visitId:v1";
const CONVERSION_SENT_PREFIX = "conversionSent:v1:";
const FORM_ATTRIBUTION_PREFIX = "formAttribution:v1:";

// Only the params we care about for attribution
const TRACKING_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",

    "first_utm_source",
    "first_utm_medium",
    "first_utm_campaign",
    "first_utm_term",

    "gclid",
    "fbclid",
    "_fbc",
    "_fbp",

    "pysTrafficSource",
    "last_pysTrafficSource",

    "organic_source",
    "first_traffic_source",

    "handl_landing_page",
    "handl_landing_page_base",
    "handl_ref",
    "handl_original_ref",
    "handl_ref_domain",
    "handl_url",
    "handl_url_base",
    "handl_ip",
    "handlID",

    "traffic_source",
];

function isValid(v: string | null) {
    return !!v && v !== "null" && v !== "undefined";
}

function mergeIntoSnapshot(
    snapshot: URLSearchParams,
    incoming: URLSearchParams
): URLSearchParams {
    for (const key of TRACKING_KEYS) {
        const value = incoming.get(key);
        if (isValid(value)) {
            snapshot.set(key, value!);
        }
    }
    return snapshot;
}

function buildSnapshot(params: URLSearchParams): URLSearchParams {
    return mergeIntoSnapshot(new URLSearchParams(), params);
}

function createFallbackId(prefix: string): string {
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Returns a stable tracking snapshot for the whole visit.
 * Merges new params into any existing snapshot so late-arriving UTMs are kept.
 */
export function getOrCreateTrackingSnapshot(
    mergedParams: URLSearchParams
): URLSearchParams {
    const existing = sessionStorage.getItem(TRACKING_SNAPSHOT_KEY);
    const snapshot = existing
        ? mergeIntoSnapshot(new URLSearchParams(existing), mergedParams)
        : buildSnapshot(mergedParams);

    sessionStorage.setItem(TRACKING_SNAPSHOT_KEY, snapshot.toString());
    return snapshot;
}

/**
 * Saved when a form is created so QuoteSubmitted uses the same params/visitId
 * even if session storage is reset after client-side navigation.
 */
export function saveFormAttribution(
    formId: number,
    params: URLSearchParams,
    visitId: string
): void {
    if (!formId) return;

    sessionStorage.setItem(
        `${FORM_ATTRIBUTION_PREFIX}${formId}`,
        JSON.stringify({
            params: params.toString(),
            visitId: visitId || "",
        })
    );
}

export function loadFormAttribution(
    formId: number
): { params: URLSearchParams; visitId: string } | null {
    if (!formId) return null;

    const raw = sessionStorage.getItem(`${FORM_ATTRIBUTION_PREFIX}${formId}`);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as { params?: string; visitId?: string };
        return {
            params: new URLSearchParams(parsed.params || ""),
            visitId: parsed.visitId || "",
        };
    } catch {
        return null;
    }
}

export function getTrackingParamsForFormSubmit(formId: number): {
    params: URLSearchParams;
    visitId: string;
} {
    const saved = loadFormAttribution(formId);
    const snapshot = getOrCreateTrackingSnapshot(
        new URLSearchParams(window.location.search)
    );

    if (saved?.params.toString()) {
        const merged = new URLSearchParams(saved.params.toString());
        snapshot.forEach((value, key) => {
            if (isValid(value) && !merged.has(key)) {
                merged.set(key, value);
            }
        });
        return {
            params: merged,
            visitId: saved.visitId || getOrCreateVisitId(),
        };
    }

    return {
        params: snapshot,
        visitId: getOrCreateVisitId(),
    };
}

/**
 * Optional helper if you ever want to reset tracking
 * (e.g. debugging or special flows)
 */
export function clearTrackingSnapshot() {
    sessionStorage.removeItem(TRACKING_SNAPSHOT_KEY);
}

export function registerFormCreationForVisit() {
    const prev = parseInt(sessionStorage.getItem(FORM_CREATE_COUNT_KEY) || "0", 10);

    const current = prev + 1;
    sessionStorage.setItem(FORM_CREATE_COUNT_KEY, String(current));

    return {
        creationIndex: current,
        isDuplicate: current > 1,
    };
}

export function getOrCreateVisitId(): string {
    let id = sessionStorage.getItem(VISIT_ID_KEY);
    if (id) return id;

    id = createFallbackId("visit");
    sessionStorage.setItem(VISIT_ID_KEY, id);
    return id;
}

export function createMessageId(): string {
    return createFallbackId("msg");
}

export function getConversionStorageKey(transactionId: string): string {
    return `${CONVERSION_SENT_PREFIX}${transactionId}`;
}

export function hasConversionBeenSent(transactionId: string): boolean {
    return sessionStorage.getItem(getConversionStorageKey(transactionId)) === "1";
}

export function markConversionAsSent(transactionId: string): void {
    sessionStorage.setItem(getConversionStorageKey(transactionId), "1");
}

export function clearConversionSent(transactionId: string): void {
    sessionStorage.removeItem(getConversionStorageKey(transactionId));
}
